// "Mostrar mais" do grid de coleções: um card depois do último produto que ocupa o resto da última fileira.
// Quando o card chega perto da tela, busca a próxima página (fotos dos próximos modelos); o clique acrescenta os produtos antes do card.
(() => {
  if (window.__collectionLoadMore) return;
  window.__collectionLoadMore = true;

  const pages = new Map();

  const loadPage = (href, sectionId) => {
    if (!pages.has(href)) {
      const url = new URL(href, window.location.origin);
      url.searchParams.set('section_id', sectionId);
      const page = fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => new DOMParser().parseFromString(html, 'text/html'));
      page.catch(() => pages.delete(href));
      pages.set(href, page);
    }
    return pages.get(href);
  };

  // Estica o card pelas colunas que sobram na última fileira; com a fileira cheia, fica na largura toda (padrão do CSS).
  const fit = (card) => {
    const list = card.parentElement;
    if (!list) return;
    const styles = getComputedStyle(list);
    const tracks = styles.gridTemplateColumns.split(' ').filter(Boolean);
    const columnWidth = parseFloat(tracks[0]);
    const gap = parseFloat(styles.columnGap) || 0;
    const items = Array.from(list.children).filter((item) => item !== card && item.offsetParent);
    if (!items.length || !columnWidth) return;

    const lastTop = Math.max(...items.map((item) => item.offsetTop));
    const used = items
      .filter((item) => item.offsetTop === lastTop)
      .reduce((total, item) => total + Math.round((item.offsetWidth + gap) / (columnWidth + gap)), 0);
    const free = tracks.length - (used % tracks.length);
    const span = free === tracks.length ? '' : `span ${free}`;
    if (card.style.gridColumn !== span) card.style.gridColumn = span;
  };

  const showThumbs = async (card) => {
    const button = card.querySelector('[data-load-more-next]');
    const thumbs = card.querySelector('[data-load-more-thumbs]');
    if (!button || !thumbs || thumbs.dataset.page === button.href) return;
    thumbs.dataset.page = button.href;

    try {
      const doc = await loadPage(button.href, card.dataset.sectionId);
      if (thumbs.dataset.page !== button.href) return;
      const images = Array.from(doc.querySelectorAll('.product-list__inner > product-item'))
        .map((item) => item.querySelector('.product-item__primary-image:not([hidden])')?.getAttribute('src'))
        .filter(Boolean)
        .slice(0, 4)
        .map((path) => {
          const src = new URL(path, window.location.origin);
          src.searchParams.set('width', '160');
          return Object.assign(document.createElement('img'), { src: src.href, alt: '', width: 76, height: 76 });
        });
      thumbs.replaceChildren(...images);
      thumbs.hidden = !images.length;
    } catch (error) {
      delete thumbs.dataset.page;
    }
  };

  const nearScreen = new IntersectionObserver(
    (entries) => entries.forEach((entry) => entry.isIntersecting && showThumbs(entry.target)),
    { rootMargin: '800px 0px' }
  );

  const cards = new WeakSet();
  let frame = 0;

  // Cada card acompanha o tamanho do grid (colunas mudam com a largura, produtos novos mudam a altura).
  const watch = () => {
    frame = 0;
    document.querySelectorAll('[data-load-more]').forEach((card) => {
      if (cards.has(card) || !card.parentElement) return;
      cards.add(card);
      nearScreen.observe(card);

      let pending = 0;
      const resize = new ResizeObserver(() => {
        cancelAnimationFrame(pending);
        pending = requestAnimationFrame(() => {
          if (card.isConnected) return fit(card);
          resize.disconnect();
          nearScreen.unobserve(card);
        });
      });
      resize.observe(card.parentElement);
    });
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(watch);
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-load-more-next]');
    if (!button || button.hasAttribute('aria-busy')) return;

    const card = button.closest('[data-load-more]');
    const list = card?.closest('.product-list__inner');
    if (!list) return;

    event.preventDefault();
    button.setAttribute('aria-busy', 'true');
    button.querySelector('[data-load-more-label]').textContent = 'Carregando…';

    try {
      const doc = await loadPage(button.href, card.dataset.sectionId);
      pages.delete(button.href);

      doc.querySelectorAll('.product-list__inner > product-item').forEach((item) => {
        item.removeAttribute('reveal');
        item.classList.add('product-item--appended');
        list.insertBefore(document.importNode(item, true), card);
      });

      const next = doc.querySelector('[data-load-more]');
      if (!next) {
        card.remove();
        return;
      }

      ['[data-load-more-left]', '[data-load-more-count]'].forEach((selector) => {
        card.querySelector(selector).textContent = next.querySelector(selector).textContent;
      });
      card.querySelector('[data-load-more-bar]').style.width = next.querySelector('[data-load-more-bar]').style.width;

      const thumbs = card.querySelector('[data-load-more-thumbs]');
      thumbs.hidden = true;
      thumbs.replaceChildren();

      const nextButton = next.querySelector('[data-load-more-next]');
      button.href = nextButton.getAttribute('href');
      button.innerHTML = nextButton.innerHTML;
      button.removeAttribute('aria-busy');
      fit(card);

      // Observar de novo dispara já com a posição atual: as fotos da página seguinte vêm quando o card voltar a ficar perto.
      nearScreen.unobserve(card);
      nearScreen.observe(card);
    } catch (error) {
      // Falhou a busca: segue o link normalmente para a próxima página.
      window.location.href = button.href;
    }
  });

  const start = () => {
    watch();
    // Filtros e ordenação trocam o grid inteiro: o card novo chega junto.
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

// Nome dos cards: cada fileira reserva só a altura do maior nome (+ designer) daquela fileira,
// para os preços ficarem alinhados sem sobrar espaço nas fileiras de nomes curtos.
(() => {
  if (window.__cardNameRows) return;
  window.__cardNameRows = true;

  const lists = new Set();
  let frame = 0;

  const align = () => {
    frame = 0;
    lists.forEach((list) => {
      if (!list.isConnected) return lists.delete(list);
      const names = Array.from(list.querySelectorAll('.product-item-meta__name'));
      names.forEach((name) => name.style.removeProperty('--card-name-h'));

      const rows = new Map();
      names.forEach((name) => {
        const card = name.closest('.product-item');
        if (!card || !card.offsetParent) return;
        const top = Math.round(card.getBoundingClientRect().top + window.scrollY);
        if (!rows.has(top)) rows.set(top, []);
        rows.get(top).push(name);
      });

      rows.forEach((row) => {
        if (row.length < 2) return;
        const tallest = Math.max(...row.map((name) => name.getBoundingClientRect().height));
        row.forEach((name) => name.style.setProperty('--card-name-h', `${Math.ceil(tallest)}px`));
      });
    });
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(align);
  };

  const resize = new ResizeObserver(schedule);
  const mutations = new MutationObserver(schedule);

  const watch = (root = document) => {
    root.querySelectorAll('.product-item-meta__name').forEach((name) => {
      const list = name.closest('.product-item')?.parentElement;
      if (!list || lists.has(list)) return;
      lists.add(list);
      resize.observe(list);
      mutations.observe(list, { childList: true });
    });
    schedule();
  };

  const start = () => {
    watch();
    // Seções que chegam depois (recomendações, filtros, Mostrar mais).
    let pending = false;
    new MutationObserver(() => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        watch();
      });
    }).observe(document.body, { childList: true, subtree: true });
    document.fonts?.ready.then(schedule);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
